/**
 * Ogives: cumulative frequency graphs, and quartiles read off them.
 *
 * An ogive plots the running total of a frequency table against the *upper boundary* of
 * each class — "how many values are below this?" — and joins the points with straight
 * lines, starting from zero at the lower boundary of the first class. The straight lines
 * assume values are spread evenly inside each class, which is the same assumption the
 * grouped-data quartile formula makes:
 *
 *     Q = L + ((p·n − F) ÷ f) × w
 *
 * with L the class's lower boundary, F the total below it, f its frequency and w its
 * width. Reading a value off the curve at height p·n is exactly that formula, so the
 * graph and the arithmetic always agree.
 *
 * The classes come from one of two places: a class table typed as `10-20: 5` (class
 * limits such as 10–19, 20–29 are turned into boundaries at the midpoints, 9.5, 19.5,
 * 29.5), or raw data grouped by the same bin rules the histogram maker uses.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { roundTo } from "@/lib/tool-kit/charts/scale";

export interface OgiveData {
  /** n + 1 class boundaries for n classes. */
  edges: number[];
  counts: number[];
  /** Total frequency. */
  total: number;
}

/* -------------------------------------------------------------- class table */

export type ClassIssueKind =
  | "badLine"
  | "badCount"
  | "order"
  | "overlap"
  | "width";

export interface ClassIssue {
  kind: ClassIssueKind;
  line: number;
  text: string;
}

export interface ClassTable {
  data: OgiveData | null;
  /** True when gaps between class limits were closed by moving boundaries to midpoints. */
  adjusted: boolean;
  issues: ClassIssue[];
}

const LINE_BREAK = /\r\n|\r|\n/;
const COMMENT = /^\s*(?:#|\/\/)/;
const CLASS_RANGE = /^\s*(-?\d[\d.,]*)\s*(?:to|[–—~-])\s*(-?\d[\d.,]*)\s*$/i;
const LAST_SPACE = /\s+(?=\S+$)/;

interface ClassRow {
  lower: number;
  upper: number;
  count: number;
  line: number;
}

/** Split a line at its last `:`, `;`, tab or comma, else at its last space. */
function splitClassLine(text: string): [string, string] | null {
  for (const mark of [":", ";", "\t", ","]) {
    const at = text.lastIndexOf(mark);
    if (at > 0) {
      return [text.slice(0, at), text.slice(at + 1)];
    }
  }
  const parts = text.trim().split(LAST_SPACE);
  return parts.length === 2 ? [parts[0], parts[1]] : null;
}

function readClassRow(
  text: string,
  line: number,
  issues: ClassIssue[]
): ClassRow | null {
  const split = splitClassLine(text);
  const range = split ? CLASS_RANGE.exec(split[0]) : null;
  if (!(split && range)) {
    issues.push({ kind: "badLine", line, text: text.trim() });
    return null;
  }
  const lower = parseNumberInput(range[1]);
  const upper = parseNumberInput(range[2]);
  const count = parseNumberInput(split[1]);
  if (lower === null || upper === null || !(upper > lower)) {
    issues.push({ kind: "width", line, text: text.trim() });
    return null;
  }
  if (count === null || count < 0) {
    issues.push({ kind: "badCount", line, text: text.trim() });
    return null;
  }
  return { lower, upper, count, line };
}

function boundaries(rows: readonly ClassRow[]): number[] {
  const edges: number[] = [];
  const gapAfter = (i: number) => rows[i + 1].lower - rows[i].upper;
  const firstGap = rows.length > 1 ? gapAfter(0) : 0;
  edges.push(rows[0].lower - firstGap / 2);
  for (let i = 1; i < rows.length; i++) {
    edges.push((rows[i - 1].upper + rows[i].lower) / 2);
  }
  const lastGap = rows.length > 1 ? gapAfter(rows.length - 2) : 0;
  edges.push((rows.at(-1)?.upper ?? 0) + lastGap / 2);
  return edges.map((edge) => roundTo(edge, 8));
}

/** Read a class table: `lower-upper: frequency` per line, lowest class first. */
export function parseClassTable(text: string): ClassTable {
  const issues: ClassIssue[] = [];
  const rows: ClassRow[] = [];
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() === "" || COMMENT.test(raw)) {
      continue;
    }
    const row = readClassRow(raw, index + 1, issues);
    if (row) {
      rows.push(row);
    }
  }
  if (rows.length === 0) {
    return { data: null, adjusted: false, issues };
  }
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].lower < rows[i - 1].lower) {
      issues.push({
        kind: "order",
        line: rows[i].line,
        text: `${rows[i].lower}`,
      });
      return { data: null, adjusted: false, issues };
    }
    if (rows[i].lower < rows[i - 1].upper) {
      issues.push({
        kind: "overlap",
        line: rows[i].line,
        text: `${rows[i - 1].upper}`,
      });
      return { data: null, adjusted: false, issues };
    }
  }
  const edges = boundaries(rows);
  const adjusted = rows.some(
    (row, i) => i > 0 && row.lower > rows[i - 1].upper
  );
  const counts = rows.map((row) => row.count);
  return {
    data: {
      edges,
      counts,
      total: counts.reduce((sum, count) => sum + count, 0),
    },
    adjusted,
    issues,
  };
}

/* ----------------------------------------------------------------- the curve */

/** Running totals: `cumulative(counts)[k]` is the frequency below class `k`'s lower boundary. */
export function cumulative(counts: readonly number[]): number[] {
  const totals = [0];
  for (const count of counts) {
    totals.push((totals.at(-1) ?? 0) + count);
  }
  return totals;
}

/** The ogive's points: (lower boundary, 0), then (each upper boundary, running total). */
export function ogivePoints(data: OgiveData): [number, number][] {
  const totals = cumulative(data.counts);
  return data.edges.map((edge, i) => [edge, totals[i]]);
}

export interface CurveReading {
  /** The share of the data below the value, 0 to 1. */
  p: number;
  /** The height read at: `p × total`. */
  target: number;
  /** The value on the x axis where the curve reaches that height. */
  value: number;
  /** Index of the class the curve is in at that height. */
  classIndex: number;
  /** L, f, F and w of the grouped-data formula. */
  lower: number;
  frequency: number;
  before: number;
  width: number;
}

/** Read the curve at `p × total`: the grouped-data quantile, by linear interpolation. */
export function readCurve(data: OgiveData, p: number): CurveReading | null {
  if (!(data.total > 0) || p < 0 || p > 1) {
    return null;
  }
  const totals = cumulative(data.counts);
  const target = p * data.total;
  const k = data.counts.findIndex(
    (count, i) => count > 0 && totals[i + 1] >= target - 1e-9
  );
  if (k < 0) {
    return null;
  }
  const lower = data.edges[k];
  const width = data.edges[k + 1] - lower;
  const frequency = data.counts[k];
  const before = totals[k];
  const along = Math.min(1, Math.max(0, (target - before) / frequency));
  return {
    p,
    target,
    value: lower + along * width,
    classIndex: k,
    lower,
    frequency,
    before,
    width,
  };
}

export interface ClassRowText {
  from: number;
  to: number;
  frequency: number;
  cumulative: number;
  /** Cumulative share, 0 to 100. */
  percent: number;
}

/** The table behind the graph. */
export function ogiveRows(data: OgiveData): ClassRowText[] {
  const totals = cumulative(data.counts);
  return data.counts.map((count, i) => ({
    from: data.edges[i],
    to: data.edges[i + 1],
    frequency: count,
    cumulative: totals[i + 1],
    percent: data.total > 0 ? (totals[i + 1] / data.total) * 100 : 0,
  }));
}
